import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import ClearIcon from "@mui/icons-material/Clear";
import IconButton from "@mui/material/IconButton";
import InputAdornment from "@mui/material/InputAdornment";
import Popover from "@mui/material/Popover";
import TextField from "@mui/material/TextField";
import { format } from "date-fns";
import { subMonths } from "date-fns/subMonths";
import React, { useState } from "react";
import { DateRange, DayPicker } from "react-day-picker";

import "react-day-picker/dist/style.css";

interface DateRangePickerInputProps {
  value: DateRange | undefined;
  onChange: (range: DateRange | undefined) => void;
  disableFuture?: boolean;
  disabled?: boolean;
}

export const DateRangePicker: React.FC<DateRangePickerInputProps> = ({
  value,
  onChange,
  disableFuture,
  disabled = false,
}) => {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [selectionStep, setSelectionStep] = useState<"start" | "end">("start");

  const open = Boolean(anchorEl);
  const id = open ? "date-range-popover" : undefined;

  const formattedRange =
    value?.from && value?.to
      ? `${format(value.from, "MM/dd/yyyy")} – ${format(
          value.to,
          "MM/dd/yyyy",
        )}`
      : "";

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    if (!disabled) {
      setAnchorEl(event.currentTarget);
      setSelectionStep("start");
    }
  };

  const handleClose = () => {
    setAnchorEl(null);
    setSelectionStep("start");
  };

  const handleSelect = (selected: DateRange | undefined) => {
    if (selected?.from && selected?.to) {
      onChange(selected);
    }

    if (selectionStep === "start") {
      setSelectionStep("end");
    } else {
      handleClose();
    }
  };

  const handleClear = (e: React.MouseEvent) => {
    e.stopPropagation();
    onChange(undefined);
    setSelectionStep("start");
  };

  const disabledDays = disableFuture ? { after: new Date() } : undefined;
  const defaultMonth =
    value?.from ?? (disableFuture ? subMonths(new Date(), 1) : new Date());

  return (
    <>
      <TextField
        value={formattedRange}
        onClick={handleClick}
        fullWidth
        placeholder='MM/DD/YYYY – MM/DD/YYYY'
        InputProps={{
          readOnly: true,
          endAdornment: (
            <InputAdornment position='end'>
              {value?.from && value?.to && (
                <IconButton
                  onClick={handleClear}
                  edge='end'
                  size='small'
                  data-testid='clearRange'
                >
                  <ClearIcon fontSize='small' />
                </IconButton>
              )}
              <IconButton
                onClick={handleClick}
                edge='end'
                size='small'
                disabled={disabled}
                data-testid='calendarIcon'
              >
                <CalendarTodayIcon fontSize='small' />
              </IconButton>
            </InputAdornment>
          ),
        }}
        className='text-sm'
        disabled={disabled}
        data-testid='rangePickerInput'
      />

      <Popover
        id={id}
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "left",
        }}
        data-testid='rangePickerPopover'
      >
        <div className='p-4'>
          <DayPicker
            mode='range'
            selected={value}
            onSelect={handleSelect}
            numberOfMonths={2}
            defaultMonth={defaultMonth}
            disabled={disabledDays}
            classNames={{
              day_selected: "bg-blue-600 text-white hover:bg-blue-700",
              day_range_middle: "bg-blue-100",
              day_today: "border border-blue-500",
            }}
            data-testid='rangePicker'
          />
        </div>
      </Popover>
    </>
  );
};
